/**
 * ReceivingBankAccountFormModal — cria/edita uma conta de recebimento.
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import ToggleSwitch from '../../../atoms/ToogleSwitch';
import {
    createReceivingBankAccount, updateReceivingBankAccount, setReceivingBankAccountActive,
} from '../services/receivingBankAccountApi';

interface Props {
    account: any | null;   // null = criar; objeto = editar
    onClose: () => void;
    onSuccess: () => void;
}

const ReceivingBankAccountFormModal: React.FC<Props> = ({ account, onClose, onSuccess }) => {
    const editing = !!account;
    const [form, setForm] = useState({
        exibition_name: account?.exibitionName || '',
        alias_name: account?.aliasName || '',
        bank_name: account?.bankName || '',
    });
    const [submitting, setSubmitting] = useState(false);
    // conta nova nasce ativa; na edição o toggle é o único jeito de desativar/reativar
    const wasActive = account?.active !== false;
    const [active, setActive] = useState(wasActive);

    const handleSubmit = async () => {
        if (!form.exibition_name.trim() || !form.alias_name.trim()) {
            toast.warn('Nome de exibição e apelido são obrigatórios');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                exibition_name: form.exibition_name.trim(),
                alias_name: form.alias_name.trim(),
                bank_name: form.bank_name.trim() || undefined,
            };
            if (editing) {
                const ref = account.receivingBankAccountId ?? account.id;
                await updateReceivingBankAccount(ref, payload);
                // situação tem endpoint próprio (PATCH) — só chama se mudou
                if (active !== wasActive) {
                    await setReceivingBankAccountActive(ref, active);
                }
                toast.success('Conta atualizada');
            } else {
                await createReceivingBankAccount(payload);
                toast.success('Conta criada');
            }
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao salvar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {editing ? 'Editar conta de recebimento' : 'Nova conta de recebimento'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <FaTimes size={20} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome de exibição</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.exibition_name} onChange={e => setForm({ ...form, exibition_name: e.target.value })}
                            placeholder="Ex.: Itaú SCFI" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apelido (único)</label>
                        <input type="text"
                            className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-800"
                            value={form.alias_name} onChange={e => setForm({ ...form, alias_name: e.target.value })}
                            disabled={editing}
                            placeholder="Ex.: ITAU_SCFI" />
                        <p className="text-xs text-gray-400 mt-1">
                            {editing
                                ? 'Chave de negócio: não pode ser alterada depois da criação (pagamentos apontam para ela).'
                                : 'Chave estável usada internamente; não poderá ser alterada depois.'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Banco / instituição</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })}
                            placeholder="Ex.: Itaú" />
                    </div>

                    {editing && (
                        <div className="pt-2 border-t dark:border-slate-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Situação</span>
                                    <span className={`text-xs ${active ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                        {active ? 'Ativa' : 'Desativada'}
                                    </span>
                                </div>
                                <ToggleSwitch
                                    checked={active}
                                    onChange={setActive}
                                    activeClass="bg-green-600"
                                    inactiveClass="bg-amber-500"
                                />
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                {active
                                    ? 'Desativar remove a conta da seleção de novos pagamentos. Pagamentos já registrados nela continuam intactos.'
                                    : 'Desativada: não aparece na seleção de novos pagamentos. A conta não é excluída — reative aqui quando precisar.'}
                            </p>
                        </div>
                    )}
                </div>

                <div className="mt-5 flex gap-2 justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                        {submitting ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReceivingBankAccountFormModal;
